import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { atoAPI, errMsg } from '../../lib/atoAPI';
import { connAPI } from '../../lib/dspAPI';
import { MockBanner, SimpleTable } from './AtoBits';
import { AtoCorrespondence } from './AtoCorrespondence';
import { AtoAccounts, AtoAsReports, AtoLodgments } from './AtoData';

const Section = ({ title, children }) => <section className="space-y-2"><h3 className="text-sm font-semibold">{title}</h3>{children}</section>;

export const ClientAtoTab = ({ clientId }) => {
  const [o, setO] = useState(null);
  const [access, setAccess] = useState(null);
  const [busy, setBusy] = useState(false);
  const [nonce, setNonce] = useState(0);
  const load = useCallback(() => atoAPI.clientOverview(clientId).then(setO).catch((e) => toast.error(errMsg(e))), [clientId]);
  const [auths, setAuths] = useState(null);
  const loadAuths = useCallback(() => connAPI.clientAuths(clientId).then(setAuths).catch(() => {}), [clientId]);
  useEffect(() => { atoAPI.access().then(setAccess).catch(() => {}); load(); loadAuths(); }, [load, loadAuths]);
  const setAuth = async (sid, status) => { try { await connAPI.setClientAuth(clientId, sid, status); toast.success('Recorded (source: manual)'); loadAuths(); } catch (e) { toast.error(errMsg(e)); } };
  const refresh = async () => {
    setBusy(true);
    try { const j = await atoAPI.sync(clientId); toast.success(`Refresh ${j.status.replaceAll('_', ' ')}`); await load(); setNonce((n) => n + 1); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!o) return null;
  const w = !!access?.can_write;
  return (
    <div className="space-y-6" data-testid="client-ato-tab" key={nonce}>
      <MockBanner />
      <div className="flex flex-wrap items-center gap-3 rounded-md border p-4 text-sm" data-testid="client-ato-overview">
        <div className="flex-1">
          <div><b>Connection:</b> {o.connection}</div>
          <div><b>Identifier:</b> {o.identity ? `TFN on file (encrypted)${o.identity.abn ? ` · ABN ${o.identity.abn}` : ''} · ${o.identity.agent_link}` : 'None recorded'}</div>
          <div className="text-muted-foreground">Last sync: {o.sync[0]?.started_at?.slice(0, 16).replace('T', ' ') || 'never'}</div>
        </div>
        {w && (o.refresh_available
          ? <Button onClick={refresh} disabled={busy} data-testid="client-ato-refresh"><RefreshCw className={`mr-2 h-4 w-4 ${busy ? 'animate-spin' : ''}`} />Refresh from ATO (mock)</Button>
          : <span className="text-xs text-rose-700" data-testid="client-ato-unavailable">Refresh unavailable: {o.refresh_unavailable_reason}</span>)}
      </div>
      <Section title="Client-agent authorisation (per service)">
        <p className="text-xs text-muted-foreground">{auths?.note}</p>
        <SimpleTable testId="client-ato-auths" rows={auths?.items || []} cols={[{ k: 'service_id', label: 'Service' },
          { k: 'status', label: 'Status', render: (r) => (w ? (
            <select className="rounded border bg-background px-2 py-1 text-xs" value={r.status} onChange={(e) => setAuth(r.service_id, e.target.value)} data-testid={`client-ato-auth-${r.service_id}`}>
              {['unknown', 'authorised', 'not authorised', 'error'].map((s) => <option key={s} value={s}>{s}</option>)}</select>) : r.status) },
          { k: 'source', label: 'Source', render: (r) => r.source || '—' }]} />
      </Section>
      <Section title="Correspondence"><AtoCorrespondence clientId={clientId} canWrite={w} /></Section>
      <Section title="Accounts"><AtoAccounts clientId={clientId} /></Section>
      <Section title="Lodgments"><AtoLodgments clientId={clientId} canWrite={w} /></Section>
      <Section title="BAS / IAS history"><AtoAsReports clientId={clientId} canWrite={w} /></Section>
      <Section title="ATO tasks"><SimpleTable testId="client-ato-tasks" rows={o.ato_tasks} empty="No ATO-linked tasks." cols={[{ k: 'title', label: 'Task' }, { k: 'status', label: 'Status' }]} /></Section>
      <Section title="Sync history"><SimpleTable testId="client-ato-sync" rows={o.sync} empty="No syncs for this client." cols={[{ k: 'started_at', label: 'Started', render: (r) => r.started_at?.slice(0, 16).replace('T', ' ') }, { k: 'status', label: 'Status' }]} /></Section>
    </div>
  );
};
